import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  CardActionArea,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import client from "../../../../../API";
import useAlertDialog from "../../../../../Components/useAlertDialog";
import AlertDialog from "../../../../../Components/AlertDialog";

const CollectionsDetailsView = () => {
  const queryClient = useQueryClient();
  const { dialogState, openDialog } = useAlertDialog();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  // Queries
  const { data: summary, refetch: refetchSummary, isLoading: summaryLoading } = useQuery(
    ["collectionsSummary"],
    client.invoiceCollections.getSummary
  );

  const { data: activeData, refetch: refetchActive, isLoading: activeLoading } = useQuery(
    ["collectionsActive", stageFilter],
    () => client.invoiceCollections.getActive({ stage: stageFilter === "all" ? undefined : stageFilter })
  );

  const { data: agingReport, refetch: refetchAging, isLoading: agingLoading } = useQuery(
    ["collectionsAging"],
    () => client.invoiceCollections.getAgingReport({ include_resolved: false })
  );

  const { data: topOverdue, isLoading: topLoading } = useQuery(
    ["collectionsTopOverdue"],
    () => client.invoiceCollections.getTopOverdue({ limit: 10 })
  );

  const { data: oldestOverdue, isLoading: oldestLoading } = useQuery(
    ["collectionsOldestOverdue"],
    () => client.invoiceCollections.getOldestOverdue({ limit: 10 })
  );

  const { data: recentResolutions, isLoading: resolutionsLoading } = useQuery(
    ["collectionsRecentResolutions"],
    () => client.invoiceCollections.getRecentResolutions({ days: 30 })
  );

  // Mutations
  const { mutate: resolveCollection } = useMutation(
    ({ ledgerId, data }) => client.invoiceCollections.resolve(ledgerId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("collections");
        openDialog("Success", "Collection resolved successfully");
        setResolveDialogOpen(false);
        setSelectedCollection(null);
        setSummaryDialogOpen(false);
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to resolve collection");
      },
    }
  );

  const { mutate: addContactNote } = useMutation(
    ({ ledgerId, data }) => client.invoiceCollections.addContactNote(ledgerId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("collections");
        openDialog("Success", "Contact note added successfully");
        setContactDialogOpen(false);
        setSelectedCollection(null);
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to add contact note");
      },
    }
  );

  const { mutate: bulkWriteOff } = useMutation(
    (data) => client.invoiceCollections.bulkWriteOff(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("collections");
        openDialog("Success", `Successfully wrote off ${data.success_count} collections`);
        setSelectedCollections([]);
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to write off collections");
      },
    }
  );

  const { mutate: runManualSweep, isLoading: sweepLoading } = useMutation(
    () => client.invoiceCollections.manualSweep(),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("collections");
        openDialog(
          "Success",
          `Collections sweep completed successfully at ${new Date(data.timestamp).toLocaleString()}`
        );
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || "Failed to run collections sweep"
        );
      },
    }
  );

  // Helper functions
  const formatAmount = (amt) =>
    amt == null
      ? "N/A"
      : `$${parseFloat(amt)
          .toFixed(2)
          .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  const formatCompact = (amt) => {
    if (amt == null) return "N/A";
    const num = parseFloat(amt);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case "30_days_overdue":
        return "warning";
      case "60_days_overdue":
        return "error";
      case "90_days_overdue":
        return "error";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  const getStageLabel = (stage) => {
    return stage?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "Unknown";
  };

  const handleRefreshAll = () => {
    refetchSummary();
    refetchActive();
    refetchAging();
    queryClient.invalidateQueries("collectionsTopOverdue");
    queryClient.invalidateQueries("collectionsOldestOverdue");
    queryClient.invalidateQueries("collectionsRecentResolutions");
  };

  const handleManualSweep = () => {
    if (window.confirm(
      "This will manually run the collections sweep process to check for overdue invoices and update collection stages. Continue?"
    )) {
      runManualSweep();
    }
  };

  const handleResolveClick = (collection) => {
    setSelectedCollection(collection);
    setResolveDialogOpen(true);
  };

  const handleContactClick = (collection) => {
    setSelectedCollection(collection);
    setContactDialogOpen(true);
  };

  const handleDetailClick = (collection) => {
    setSelectedCollection(collection);
    setDetailDialogOpen(true);
  };

  const handleBulkWriteOff = () => {
    if (selectedCollections.length === 0) {
      openDialog("Warning", "Please select collections to write off");
      return;
    }

    if (window.confirm(`Are you sure you want to write off ${selectedCollections.length} collection(s)?`)) {
      bulkWriteOff({
        ledger_ids: selectedCollections,
        notes: "Bulk write-off from admin interface",
      });
    }
  };

  const handleSummaryCardClick = (stage) => {
    setSelectedStage(stage);
    setSummaryDialogOpen(true);
  };

  const getInvoicesForStage = () => {
    if (!selectedStage || !agingReport) return [];
    
    if (selectedStage === "all") {
      // Combine all invoices from all stages
      const allInvoices = [];
      Object.keys(agingReport).forEach((stage) => {
        if (Array.isArray(agingReport[stage])) {
          allInvoices.push(...agingReport[stage]);
        }
      });
      return allInvoices;
    }
    
    return agingReport[selectedStage] || [];
  };

  const filteredCollections = activeData?.collections?.filter(
    (c) =>
      searchTerm === "" ||
      c.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.client_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <AlertDialog alertState={dialogState} />

      {/* Header */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid #e0e0e0",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}
            >
              Collections Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor and manage overdue invoices
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={sweepLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleManualSweep}
              disabled={sweepLoading}
              sx={{ borderRadius: 2 }}
            >
              {sweepLoading ? "Running..." : "Run Sweep"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshAll}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
            {selectedCollections.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkWriteOff}
              >
                Write Off ({selectedCollections.length})
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 4, py: 3 }}>
        {/* Summary Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card 
            sx={{ 
              borderRadius: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <CardActionArea onClick={() => handleSummaryCardClick("30_days_overdue")}>
              <CardContent>
                {summaryLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        30 Days Overdue
                      </Typography>
                      <Tooltip title="Invoices 30-59 days past due - Click to view list" arrow>
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#f59e0b", mb: 0.5 }}
                    >
                      {formatCompact(summary?.["30_days_overdue"]?.total || 0)}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {summary?.["30_days_overdue"]?.count || 0} invoices
                      </Typography>
                      <TrendingUpIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                    </Box>
                  </>
                )}
              </CardContent>
            </CardActionArea>
          </Card>

          <Card 
            sx={{ 
              borderRadius: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <CardActionArea onClick={() => handleSummaryCardClick("60_days_overdue")}>
              <CardContent>
                {summaryLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        60 Days Overdue
                      </Typography>
                      <Tooltip title="Invoices 60-89 days past due - Click to view list" arrow>
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#ef4444", mb: 0.5 }}
                    >
                      {formatCompact(summary?.["60_days_overdue"]?.total || 0)}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {summary?.["60_days_overdue"]?.count || 0} invoices
                      </Typography>
                      <TrendingUpIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    </Box>
                  </>
                )}
              </CardContent>
            </CardActionArea>
          </Card>

          <Card 
            sx={{ 
              borderRadius: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <CardActionArea onClick={() => handleSummaryCardClick("90_days_overdue")}>
              <CardContent>
                {summaryLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        90+ Days Overdue
                      </Typography>
                      <Tooltip title="Invoices 90+ days past due - Click to view list" arrow>
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#dc2626", mb: 0.5 }}
                    >
                      {formatCompact(summary?.["90_days_overdue"]?.total || 0)}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {summary?.["90_days_overdue"]?.count || 0} invoices
                      </Typography>
                      <TrendingUpIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                    </Box>
                  </>
                )}
              </CardContent>
            </CardActionArea>
          </Card>

          <Card 
            sx={{ 
              borderRadius: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <CardActionArea onClick={() => handleSummaryCardClick("all")}>
              <CardContent>
                {summaryLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Total Outstanding
                      </Typography>
                      <Tooltip title="Total amount across all overdue invoices - Click to view all" arrow>
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}
                    >
                      {formatCompact(summary?.overall?.total || 0)}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {summary?.overall?.count || 0} total invoices
                      </Typography>
                      <TrendingUpIcon sx={{ fontSize: 16, color: "#1a1a1a" }} />
                    </Box>
                  </>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>

        {/* Main Content Tabs */}
        <Card sx={{ borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label="Active Collections" />
            <Tab label="Aging Report" />
            <Tab label="Top Overdue" />
            <Tab label="Recent Resolutions" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Active Collections Tab */}
            {activeTab === 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search PO#, client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 300 }}
                  />

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Stage Filter</InputLabel>
                    <Select
                      value={stageFilter}
                      label="Stage Filter"
                      onChange={(e) => setStageFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Stages</MenuItem>
                      <MenuItem value="30_days_overdue">30 Days Overdue</MenuItem>
                      <MenuItem value="60_days_overdue">60 Days Overdue</MenuItem>
                      <MenuItem value="90_days_overdue">90+ Days Overdue</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {activeLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredCollections.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No collections found
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                selectedCollections.length === filteredCollections.length &&
                                filteredCollections.length > 0
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCollections(
                                    filteredCollections.map((c) => c.id)
                                  );
                                } else {
                                  setSelectedCollections([]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>PO Number</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Days Overdue</TableCell>
                          <TableCell>Stage</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCollections.map((collection) => (
                          <TableRow
                            key={collection.id}
                            hover
                            selected={selectedCollections.includes(collection.id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedCollections.includes(collection.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCollections([
                                      ...selectedCollections,
                                      collection.id,
                                    ]);
                                  } else {
                                    setSelectedCollections(
                                      selectedCollections.filter(
                                        (id) => id !== collection.id
                                      )
                                    );
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {collection.po_number}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {collection.invoice_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {collection.client_name || "N/A"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {collection.client_email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {formatAmount(collection.amount_due)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`${collection.days_overdue} days`}
                                size="small"
                                color={
                                  collection.days_overdue >= 90
                                    ? "error"
                                    : collection.days_overdue >= 60
                                    ? "warning"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStageLabel(collection.current_stage)}
                                size="small"
                                color={getStageColor(collection.current_stage)}
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(collection.original_due_date)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDetailClick(collection)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Add Contact Note">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleContactClick(collection)}
                                  >
                                    <EmailIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Resolve">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleResolveClick(collection)}
                                    color="success"
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* Aging Report Tab */}
            {activeTab === 1 && (
              <Box>
                {agingLoading ? (
                  <CircularProgress />
                ) : (
                  <Grid container spacing={3}>
                    {Object.entries(agingReport || {}).map(([stage, collections]) => {
                      if (Array.isArray(collections) && collections.length > 0) {
                        const total = collections.reduce(
                          (sum, c) => sum + parseFloat(c.amount_due || 0),
                          0
                        );
                        return (
                          <Grid item xs={12} md={6} key={stage}>
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 2, fontWeight: 600 }}
                                >
                                  {getStageLabel(stage)}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  sx={{ mb: 1, fontWeight: 700 }}
                                >
                                  {formatAmount(total)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  {collections.length} invoices
                                </Typography>
                                <List dense>
                                  {collections.slice(0, 5).map((c) => (
                                    <ListItem key={c.id} disablePadding>
                                      <ListItemText
                                        primary={c.po_number}
                                        secondary={`${formatAmount(c.amount_due)} - ${
                                          c.client_name
                                        }`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                                {collections.length > 5 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    +{collections.length - 5} more
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      }
                      return null;
                    })}
                  </Grid>
                )}
              </Box>
            )}

            {/* Top Overdue Tab */}
            {activeTab === 2 && (
              <Box>
                {topLoading ? (
                  <CircularProgress />
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>PO Number</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Days Overdue</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topOverdue?.collections?.map((collection, index) => (
                          <TableRow key={collection.id}>
                            <TableCell>
                              <Chip
                                label={`#${index + 1}`}
                                size="small"
                                color={index < 3 ? "error" : "default"}
                              />
                            </TableCell>
                            <TableCell>{collection.po_number}</TableCell>
                            <TableCell>{collection.client_name}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: "#dc2626" }}
                              >
                                {formatAmount(collection.amount_due)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {collection.days_overdue} days
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => handleDetailClick(collection)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Recent Resolutions Tab */}
            {activeTab === 3 && (
              <Box>
                {resolutionsLoading ? (
                  <CircularProgress />
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>PO Number</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Resolution Type</TableCell>
                          <TableCell>Resolved Date</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentResolutions?.resolutions?.map((resolution) => (
                          <TableRow key={resolution.id}>
                            <TableCell>{resolution.po_number}</TableCell>
                            <TableCell>{resolution.client_name}</TableCell>
                            <TableCell>
                              {formatAmount(resolution.amount_due)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={resolution.resolution_type}
                                size="small"
                                color={
                                  resolution.resolution_type === "paid"
                                    ? "success"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(resolution.resolved_at)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {resolution.resolution_notes || "N/A"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Summary Stage Dialog */}
      <SummaryStageDialog
        open={summaryDialogOpen}
        stage={selectedStage}
        invoices={getInvoicesForStage()}
        onClose={() => {
          setSummaryDialogOpen(false);
          setSelectedStage(null);
        }}
        formatAmount={formatAmount}
        formatDate={formatDate}
        getStageLabel={getStageLabel}
        getStageColor={getStageColor}
        onResolve={handleResolveClick}
        onContact={handleContactClick}
        onDetail={handleDetailClick}
      />

      {/* Resolve Dialog */}
      <ResolveDialog
        open={resolveDialogOpen}
        collection={selectedCollection}
        onClose={() => {
          setResolveDialogOpen(false);
          setSelectedCollection(null);
        }}
        onSubmit={(data) => {
          if (selectedCollection) {
            resolveCollection({ ledgerId: selectedCollection.id, data });
          }
        }}
      />

      {/* Contact Dialog */}
      <ContactDialog
        open={contactDialogOpen}
        collection={selectedCollection}
        onClose={() => {
          setContactDialogOpen(false);
          setSelectedCollection(null);
        }}
        onSubmit={(data) => {
          if (selectedCollection) {
            addContactNote({ ledgerId: selectedCollection.id, data });
          }
        }}
      />

      {/* Detail Dialog */}
      <DetailDialog
        open={detailDialogOpen}
        collection={selectedCollection}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedCollection(null);
        }}
        formatAmount={formatAmount}
        formatDate={formatDate}
      />
    </Box>
  );
};

// Summary Stage Dialog Component
const SummaryStageDialog = ({
  open,
  stage,
  invoices,
  onClose,
  formatAmount,
  formatDate,
  getStageLabel,
  getStageColor,
  onResolve,
  onContact,
  onDetail,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredInvoices = invoices.filter(
    (invoice) =>
      searchTerm === "" ||
      invoice.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAmount = filteredInvoices.reduce(
    (sum, invoice) => sum + parseFloat(invoice.amount_due || 0),
    0
  );

  const getDialogTitle = () => {
    if (stage === "all") return "All Outstanding Invoices";
    return `${getStageLabel(stage)} - Invoices to Reconcile`;
  };

  const getDialogColor = () => {
    if (stage === "all") return "#1a1a1a";
    if (stage === "90_days_overdue") return "#dc2626";
    if (stage === "60_days_overdue") return "#ef4444";
    if (stage === "30_days_overdue") return "#f59e0b";
    return "#1a1a1a";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: getDialogColor(), fontWeight: 700 }}>
              {getDialogTitle()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? "s" : ""} · {formatAmount(totalAmount)} total
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            size="small"
            placeholder="Search PO#, client name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {filteredInvoices.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No invoices found
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Amount Due</TableCell>
                    <TableCell>Days Overdue</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {invoice.po_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.invoice_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.client_name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.client_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ 
                            fontWeight: 700,
                            color: invoice.days_overdue >= 90 ? "#dc2626" : 
                                   invoice.days_overdue >= 60 ? "#ef4444" : "#f59e0b"
                          }}
                        >
                          {formatAmount(invoice.amount_due)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${invoice.days_overdue} days`}
                          size="small"
                          color={
                            invoice.days_overdue >= 90
                              ? "error"
                              : invoice.days_overdue >= 60
                              ? "warning"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStageLabel(invoice.current_stage)}
                          size="small"
                          color={getStageColor(invoice.current_stage)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(invoice.original_due_date)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                onDetail(invoice);
                                onClose();
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Contact Note">
                            <IconButton
                              size="small"
                              onClick={() => {
                                onContact(invoice);
                                onClose();
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Resolve">
                            <IconButton
                              size="small"
                              onClick={() => {
                                onResolve(invoice);
                              }}
                              color="success"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Resolve Dialog Component
const ResolveDialog = ({ open, collection, onClose, onSubmit }) => {
  const [resolutionType, setResolutionType] = useState("paid");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    onSubmit({ resolution_type: resolutionType, notes });
    setNotes("");
    setResolutionType("paid");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve Collection</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {collection && (
            <Alert severity="info">
              Resolving collection for PO# {collection.po_number} - $
              {parseFloat(collection.amount_due).toFixed(2)}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel>Resolution Type</InputLabel>
            <Select
              value={resolutionType}
              label="Resolution Type"
              onChange={(e) => setResolutionType(e.target.value)}
            >
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="written_off">Written Off</MenuItem>
              <MenuItem value="dispute">Dispute</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter resolution notes..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Resolve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Contact Dialog Component
const ContactDialog = ({ open, collection, onClose, onSubmit }) => {
  const [contactType, setContactType] = useState("email");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    onSubmit({ contact_type: contactType, notes });
    setNotes("");
    setContactType("email");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Contact Note</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {collection && (
            <Alert severity="info">
              Adding contact note for PO# {collection.po_number}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel>Contact Type</InputLabel>
            <Select
              value={contactType}
              label="Contact Type"
              onChange={(e) => setContactType(e.target.value)}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
              <MenuItem value="letter">Letter</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter contact details..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Add Note
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Detail Dialog Component
const DetailDialog = ({ open, collection, onClose, formatAmount, formatDate }) => {
  if (!collection) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Collection Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                PO Number
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {collection.po_number}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Amount Due
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 700 }}>
                {formatAmount(collection.amount_due)}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Client Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {collection.client_name || "N/A"}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Client Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {collection.client_email || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Days Overdue
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {collection.days_overdue} days
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Current Stage
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={collection.current_stage?.replace(/_/g, " ")}
                  size="small"
                  color={
                    collection.current_stage === "90_days_overdue"
                      ? "error"
                      : "warning"
                  }
                />
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Original Due Date
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(collection.original_due_date)}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(collection.created_at)}
              </Typography>
            </Grid>

            {collection.contact_history &&
              collection.contact_history.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Contact History
                  </Typography>
                  <List dense>
                    {collection.contact_history.map((contact, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${contact.type} - ${new Date(
                            contact.date
                          ).toLocaleString()}`}
                          secondary={contact.notes}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionsDetailsView;